import React, { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, Play, Trophy, User, ArrowLeft, Volume2, VolumeX, Palette, Focus, Menu, X } from 'lucide-react';
import { useChildren } from '../hooks/useChildren';
import { useTheme } from '../hooks/useTheme';
import { useChildTheme } from '../hooks/useChildTheme';
import { ChildSelector } from './ChildSelector';
import { ChildThemePicker } from './ChildThemePicker';
import Logo from './Logo';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface ChildLayoutProps {}

export function ChildLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { children } = useChildren();
  const { theme, updateTheme } = useTheme();
  const [selectedChild, setSelectedChild] = useState<string | null>(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showThemePicker, setShowThemePicker] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { childTheme, loading: themeLoading } = useChildTheme(selectedChild);
  const lastAppliedThemeRef = useRef<string | null>(null);

  const navigation = [
    { name: 'Home', href: '/child', icon: Home, color: 'blue' },
    { name: 'Activities', href: '/child/activities', icon: Play, color: 'green' },
    { name: 'Focus', href: '/child/focus', icon: Focus, color: 'cyan' },
    { name: 'Achievements', href: '/child/achievements', icon: Trophy, color: 'yellow' },
    { name: 'Profile', href: '/child/profile', icon: User, color: 'purple' },
  ];

  const selectedChildData = children.find(child => child.id === selectedChild);

  // Apply child's theme when it loads or changes
  useEffect(() => {
    if (childTheme && !themeLoading) {
      // Create a unique key for this theme configuration
      const themeKey = `${childTheme.mode}-${childTheme.reducedMotion}-${childTheme.fontSize}-${childTheme.colorIntensity}`;
      
      // Only update if this exact theme configuration hasn't been applied yet
      if (lastAppliedThemeRef.current !== themeKey) {
        console.log('🎨 Applying child theme:', themeKey);
        lastAppliedThemeRef.current = themeKey;
        
        updateTheme({
          mode: childTheme.mode,
          reducedMotion: childTheme.reducedMotion,
          fontSize: childTheme.fontSize,
          colorIntensity: childTheme.colorIntensity
        });
      }
    }
  }, [childTheme, themeLoading, updateTheme]);

  if (children.length === 0) {
    return (
      <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center transition-colors duration-200">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 bg-[var(--bg-tertiary)]">
            <User className="w-12 h-12 text-[var(--text-muted)]" />
          </div>
          <h2 className="text-3xl font-bold mb-4 text-[var(--text-primary)] font-baloo">
            No Children Added Yet!
          </h2>
          <p className="text-lg mb-8 text-[var(--text-secondary)]">
            Ask a grown-up to add your profile first, then come back to start learning!
          </p>
          <button
            onClick={() => navigate('/portal')}
            className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full font-semibold hover:from-blue-600 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 text-lg font-baloo"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Go Back</span>
          </button>
        </div>
      </div>
    );
  }

  if (!selectedChild) {
    return (
      <div className="min-h-screen bg-[var(--bg-primary)] transition-colors duration-200">
        <ChildSelector
          children={children}
          onSelectChild={setSelectedChild}
          onGoBack={() => navigate('/')}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] transition-colors duration-200">
      {/* Header */}
      <header className="bg-[var(--bg-secondary)] shadow-lg border-b border-[var(--border-color)] transition-colors duration-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSelectedChild(null)}
                className="flex items-center space-x-2 px-4 py-2 text-sm transition-all duration-300 rounded-full border shadow-sm backdrop-blur-sm text-[var(--accent-blue)] hover:text-[var(--accent-blue-dark)] bg-white/60 hover:bg-[var(--accent-blue-light)] border-[var(--accent-blue-light)] hover:border-[var(--accent-blue)] hover:shadow-md"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Switch Child</span>
              </button>
              
              <div className="h-8 w-px bg-[var(--border-color)]"></div>
              
              <div>
                <Logo size="lg" variant="horizontal" className="text-[var(--text-primary)]" />
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {selectedChildData && (
                <div className="flex items-center space-x-3">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg border-4 border-white shadow-lg"
                    style={{ backgroundColor: selectedChildData.avatar_color }}
                  >
                    {selectedChildData.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="hidden sm:block">
                    <div className="text-lg font-semibold text-[var(--text-primary)] font-baloo">
                      {selectedChildData.name}
                    </div>
                    <div className="text-sm text-[var(--text-secondary)]">
                      {selectedChildData.total_points} points
                    </div>
                  </div>
                </div>
              )}
              
              {/* Theme Picker Button */}
              <button
                onClick={() => setShowThemePicker(true)}
                className="p-3 rounded-xl transition-colors bg-[var(--bg-tertiary)] text-[var(--text-primary)] hover:bg-[var(--accent-purple)] hover:text-white border border-[var(--border-color)]"
                aria-label="Change theme and accessibility settings"
              >
                <Palette className="w-6 h-6" />
              </button>
              
              <button
                onClick={() => setSoundEnabled(!soundEnabled)}
                className={`p-3 rounded-xl transition-colors border border-[var(--border-color)] ${
                  soundEnabled
                    ? 'bg-[var(--accent-green)] text-white'
                    : 'bg-[var(--bg-tertiary)] text-[var(--text-muted)]'
                }`}
              >
                {soundEnabled ? <Volume2 className="w-6 h-6" /> : <VolumeX className="w-6 h-6" />}
              </button>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-3 rounded-xl transition-colors bg-[var(--bg-tertiary)] text-[var(--text-primary)] hover:bg-[var(--accent-purple)] hover:text-white border border-[var(--border-color)]"
                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="hidden md:block bg-[var(--bg-secondary)] shadow-md border-b border-[var(--border-color)] transition-colors duration-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center space-x-2 py-4">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex flex-col items-center space-y-2 py-4 px-6 rounded-2xl text-lg font-bold transition-all duration-200 ${
                    isActive
                      ? theme.mode === 'high-contrast'
                        ? `bg-[var(--accent-${item.color})] text-black border-4 border-white shadow-lg`
                        : `bg-[var(--accent-${item.color})] text-white shadow-lg`
                      : theme.mode === 'high-contrast'
                      ? 'text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-primary)] border-2 border-[var(--border-color)]'
                      : 'text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-primary)] bg-[var(--bg-primary)] border border-[var(--border-color)] shadow-sm'
                  } ${theme.reducedMotion ? '' : 'hover:scale-105'} font-baloo`}
                >
                  <item.icon className="w-8 h-8" />
                  <span>{item.name}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
          />

          {/* Menu Panel */}
          <div className="fixed top-0 right-0 bottom-0 w-64 z-50 md:hidden transition-transform duration-300 bg-[var(--bg-secondary)] shadow-xl border-l border-[var(--border-color)]">
            <div className="flex flex-col h-full">
              {/* Close Button */}
              <div className="flex justify-end p-4">
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-2 rounded-lg transition-colors bg-[var(--bg-tertiary)] text-[var(--text-primary)] hover:bg-[var(--accent-purple)] hover:text-white"
                  aria-label="Close menu"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Navigation Links */}
              <nav className="flex-1 px-4">
                {navigation.map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`flex items-center space-x-3 py-4 px-4 rounded-xl mb-2 transition-all duration-200 font-baloo ${
                        isActive
                          ? `bg-[var(--accent-${item.color})] text-white shadow-lg`
                          : 'text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)]'
                      }`}
                    >
                      <item.icon className="w-6 h-6" />
                      <span className="font-semibold">{item.name}</span>
                    </Link>
                  );
                })}
              </nav>
            </div>
          </div>
        </>
      )}

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Outlet context={{ selectedChild, selectedChildData, soundEnabled, theme }} />
      </main>

      {/* Child Theme Picker Modal */}
      {showThemePicker && selectedChildData && (
        <ChildThemePicker 
          isOpen={showThemePicker} 
          onClose={() => setShowThemePicker(false)}
          childId={selectedChild}
          childName={selectedChildData.name}
        />
      )}
    </div>
  );
}